import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from './AuthContext'
import { supabase } from '../services/supabaseClient'
import { getLanguageByCode, isSupportedLanguage } from '../i18n/languages'

const LanguageContext = createContext()

export function LanguageProvider({ children }) {
  const { i18n } = useTranslation()
  const { profile, user } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  
  // Get current language info
  const currentLanguage = getLanguageByCode(i18n.language)
  
  // Change language and save preference
  const changeLanguage = useCallback(async (languageCode) => {
    if (!isSupportedLanguage(languageCode)) {
      console.warn(`Language ${languageCode} is not supported`)
      return false
    }
    
    setIsLoading(true)
    setError(null)
    
    try {
      // Change i18n language
      await i18n.changeLanguage(languageCode)
      
      // Save to localStorage
      localStorage.setItem('preferred_language', languageCode)
      
      // If user is logged in, save to database
      if (user && profile) {
        const { error: dbError } = await supabase
          .from('users')
          .update({ preferred_language: languageCode })
          .eq('id', profile.id)
        
        if (dbError) {
          console.error('Failed to save language preference:', dbError)
          setError('Failed to save language preference')
          // Don't throw - localStorage is already updated
        }
      }
      
      return true
    } catch (err) {
      console.error('Error changing language:', err)
      setError('Failed to change language')
      return false
    } finally {
      setIsLoading(false)
    }
  }, [i18n, user, profile])
  
  // Load user's preferred language from profile on mount
  useEffect(() => {
    if (profile?.preferred_language && isSupportedLanguage(profile.preferred_language)) {
      // Only change if different from current
      if (i18n.language !== profile.preferred_language) {
        i18n.changeLanguage(profile.preferred_language)
      }
    }
  }, [profile, i18n])
  
  const value = {
    currentLanguage,
    changeLanguage,
    isLoading,
    error,
    supportedLanguages: ['en', 'af', 'zu', 'xh']
  }
  
  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguage() {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}
